import auth from '@/auth/auth';
import { TimelineVariant } from '@/components/Timeline/allocation/types';
import { useTimelineStore } from '@/components/Timeline/Timeline.store';
import {
    CopyEventVariantAllocationsDocument,
    CreateEventVariantDocument,
    CreateEventVariantWithAllocationsFromDocument,
} from '@/generated/graphql';
import { useMyUser, waitForUser } from '@/user/composables/useMyUser';
import { useMutation } from '@vue/apollo-composable';
import { DateTime } from 'luxon';
import { computed, Ref } from 'vue';
import { mapEventVariantForTimeline } from '../event';

export function useEventVariantSelectionDropdown(
    eventId: Ref<string>,
    variantsRef: Ref<TimelineVariant[]>,
    setVariantsRef: (value: TimelineVariant[]) => void,
    updateVisibility: (params: { allocationVariantId: string; isVisible: boolean }) => void,
    timelineStart: Ref<DateTime | undefined>,
    timelineEnd: Ref<DateTime | undefined>,
    executionPhasePosition: Ref<number>,
) {
    const newVariantIndex = computed(() => (variantsRef.value.reduce((max, v) => Math.max(max, v.index), 0) ?? 0) + 1);
    const createVariantMutation = useMutation(CreateEventVariantDocument, { fetchPolicy: 'no-cache' });
    const createVariantFromMutation = useMutation(CreateEventVariantWithAllocationsFromDocument, {
        fetchPolicy: 'no-cache',
    });
    const areAllocationsEditable = auth.isAdmin();

    const store = useTimelineStore();

    const { currentUser } = useMyUser();

    async function onCreateNewVariant(params: {
        allocationVariantLabel: string;
        copyAllocationsFrom: string | null;
        onDone: (isSuccess: boolean) => void;
    }) {
        await waitForUser(currentUser);
        let result = undefined;

        const { allocationVariantLabel, copyAllocationsFrom, onDone } = params;

        if (copyAllocationsFrom) {
            result = await createVariantFromMutation.mutate({
                variantId: copyAllocationsFrom,
                createdById: currentUser.value.id,
                createdByName: currentUser.value.name,
                name: allocationVariantLabel,
            });
        } else {
            result = await createVariantMutation.mutate({
                id: eventId.value,
                name: allocationVariantLabel,
                createdById: currentUser.value.id,
                createdByName: currentUser.value.name,
            });
        }
        if (result?.errors) {
            onDone(false);
            throw new Error(`Could not create new variant: ${result.errors}`);
        }
        let resultVariant = result?.data?.create?.variant;
        if (resultVariant) {
            if (copyAllocationsFrom) {
                const copiedIntoLocationIds = [
                    ...resultVariant.areas.nodes.map((n) => n.locationId),
                    ...resultVariant.rooms.nodes.map((n) => n.locationId),
                ];
                for (const id of copiedIntoLocationIds) {
                    store.incrementLocationAllocationTotalCount(id);
                }
                if (resultVariant.referenceAllocationAreaId || resultVariant.referenceAllocationRoomId) {
                    store.incrementLocationAllocationTotalCount('reference-allocation-row');
                }
            }
            setVariantsRef([
                ...variantsRef.value,
                mapEventVariantForTimeline(
                    resultVariant,
                    timelineStart.value,
                    timelineEnd.value,
                    executionPhasePosition.value,
                    areAllocationsEditable,
                ),
            ]);
            updateVisibility({ allocationVariantId: resultVariant.id, isVisible: true });
            onDone(true);
        }
    }

    const copyAllocationsMutation = useMutation(CopyEventVariantAllocationsDocument, { fetchPolicy: 'no-cache' });

    async function onCopyAllocations(data: {
        idTargetVariant: string;
        idSourceVariant: string;
        onDone: (isSuccess: boolean) => void;
    }) {
        const { idTargetVariant, idSourceVariant, onDone } = data;

        const result = await copyAllocationsMutation.mutate({
            sourceId: idSourceVariant,
            targetId: idTargetVariant,
        });

        if (result?.errors) {
            onDone(false);
            throw new Error(
                `Copying allocations from variant "${idSourceVariant}" to variant "${idTargetVariant}" failed: ${result.errors}`,
            );
        }
        const resultVariant = result?.data?.copyEventVariantAllocations?.eventVariant;
        if (resultVariant) {
            const copiedIntoLocationIds = [
                ...resultVariant.areas.nodes.map((n) => n.locationId),
                ...resultVariant.rooms.nodes.map((n) => n.locationId),
            ];
            for (const id of copiedIntoLocationIds) {
                store.incrementLocationAllocationTotalCount(id);
            }
            if (resultVariant.referenceAllocationAreaId || resultVariant.referenceAllocationRoomId) {
                store.incrementLocationAllocationTotalCount('reference-allocation-row');
            }
            const mappedVariantResult = mapEventVariantForTimeline(
                resultVariant,
                timelineStart.value,
                timelineEnd.value,
                executionPhasePosition.value,
                areAllocationsEditable,
            );
            setVariantsRef(variantsRef.value.map((v) => (v.id === idTargetVariant ? mappedVariantResult : v)));
            onDone(true);
        }
    }

    return {
        newVariantIndex,
        onCreateNewVariant,
        onCopyAllocations,
    };
}
